import React, { useState, useEffect, useRef } from 'react';
import { Login } from '../Login/Login';
import { Calculation } from './Calculation/Calculation';
import { CalculationSidebar } from './Sidebar/CalculationSidebar';
import { getHeadingsMapping } from './utils/getHeadingsMapping';
import { getObjectFromFormData } from '../../assets/js/utils/getObjectFromEntries';
import { fetchCalculation } from './utils/fetchCalculation';
import { fetchAddToCart } from './utils/fetchAddToCart';
import { Finished } from './Finished/Finished';
import { addToCartCount } from '../../assets/js/utils/addToCartCount';
import { getSelectedVariant } from './utils/getSelectedVariant';
import { fetchUsageAmountForOrganizations } from './utils/fetchUsageAmountForOrganizations';
import { getAmountInputError } from './utils/getAmountInputError';
import { Conditional } from './Conditional/Conditional';
import { PriceCalculationContext } from './PriceCalculationContext';
import { isLoggedIn } from '../Login/utils/isLoggedIn';
import { SchoolSelection } from './SchoolSelection/SchoolSelection';
import { formatString } from '../../assets/js/utils/formatString';
import { handleGtmEvent } from '../../assets/js/utils/gtm';

function PriceCalculation({
    meData: meDataProp,
    organizationData: organizationDataProp,
    productData: productDataProp,
    sidebarData,
    forgotPasswordData,
    selectOrganizationApiUrl,
    signInApiUrl,
    productCalculationApiUrl,
    getUsageAmountForOrganizationsApiUrl,
    getConditionalProductsApiUrl,
    cartAddApiUrl,
    cartUrl,
    continueShoppingUrl,
    resourcedTexts,
}) {
    const priceCalculationRef = useRef(null);
    const [productData, setProductData] = useState(productDataProp);
    const [amountInputErrors, setAmountInputErrors] = useState(new Map());
    const [amountInputsTouched, setAmountInputsTouched] = useState(new Set());
    const [selectedVariantId, setSelectedVariantId] = useState(productData?.variantsData?.[0]?.id || null);
    const [fixedAmounts, setFixedAmounts] = useState(null);
    const [calculationIsLoadingText, setCalculationIsLoadingText] = useState(null);
    const [fixedAmountIsLoadingText, setFixedAmountIsLoadingText] = useState(null);
    const [addToCartIsLoadingText, setAddToCartIsLoadingText] = useState(null);
    const [conditionalProductsIsLoadingText, setConditionalProductsIsLoadingText] = useState(null);
    const [showAddedToCart, setShowAddedToCart] = useState(false);
    const [calculationResult, setCalculationResult] = useState(null);
    const [conditionalProductsData, setConditionalProductsData] = useState(null);
    const [isInConditionalProductsFlow, setIsInConditionalProductsFlow] = useState(false);
    const [organizationData, setOrganizationData] = useState(organizationDataProp);
    const [selectedOrganizationsIds, setSelectedOrganizationsIds] = useState([]);
    const [originalSelectedOrganizationIds, setOriginalSelectedOrganizationIds] = useState([]);

    const [activeStepName, setActiveStepName] = useState(
        isLoggedIn(meDataProp) ? 'CalculationSchoolSelection' : 'LoginComponent',
    ); // LoginComponent || CalculationSchoolSelection || CalculationCalculate || CalculationResult || CalculationConditional || CalculationFinished

    const { title, mainHeading } = getHeadingsMapping(
        activeStepName,
        resourcedTexts,
        productData?.name || '',
        selectedOrganizationsIds.length,
        conditionalProductsData?.conditionalType,
    );

    useEffect(() => {
        if (activeStepName !== 'CalculationCalculate') return;

        function resetAmountInputError() {
            setAmountInputsTouched(new Set());
            setAmountInputErrors(new Map());
        }

        resetAmountInputError();
    }, [activeStepName]);

    function scrollToTop() {
        priceCalculationRef.current.scrollIntoView();
    }

    async function handleUsageAmountForOrganizations(organizationIds, unitData) {
        const { isFixedForOrganizations, usageUnitId } = unitData;

        if (!isFixedForOrganizations || !usageUnitId) {
            setFixedAmounts(null);
            setActiveStepName('CalculationCalculate');
            return;
        }

        setFixedAmountIsLoadingText(resourcedTexts.fixedUsageAmountLoadingText);

        try {
            const responseJson = await fetchUsageAmountForOrganizations(
                getUsageAmountForOrganizationsApiUrl,
                usageUnitId,
                organizationIds,
                ({ message, percentage }) => {
                    setFixedAmountIsLoadingText(formatString(message, [percentage]));
                },
            );

            const { fixedUsageUnitAmount } = responseJson;
            setFixedAmounts([...fixedUsageUnitAmount]);
            setFixedAmountIsLoadingText(null);
            setActiveStepName('CalculationCalculate');
        } catch (error) {
            setFixedAmountIsLoadingText(null);
            setActiveStepName('CalculationTechnicalProblem');
            console.error('error', error.message);
        }
    }

    function goToSchoolSelection(organizationData) {
        if (organizationData) {
            setOrganizationData(organizationData);
        }

        setProductData(productDataProp); // Reset to the original productData (in case the user has been selecting conditional products and changed the organization).
        setActiveStepName('CalculationSchoolSelection');
    }

    async function handleSchoolSelectionSubmit(checkedOrganizations) {
        const newSelectedOrganizationIds = Object.keys(checkedOrganizations);
        setSelectedOrganizationsIds(newSelectedOrganizationIds);
        setOriginalSelectedOrganizationIds(newSelectedOrganizationIds);
        await handleUsageAmountForOrganizations(newSelectedOrganizationIds, productData.unitData);
        scrollToTop();

        handleGtmEvent('price_calculation_organization_select');
    }

    async function handleCalculation(productId, startDate, endDate, amounts, usageUnitId) {
        setCalculationIsLoadingText(resourcedTexts.calculationLoader);

        try {
            const responseJson = await fetchCalculation(
                productCalculationApiUrl,
                productId,
                selectedVariantId,
                startDate,
                endDate,
                amounts,
                usageUnitId,
                ({ message, percentage }) => {
                    setCalculationIsLoadingText(formatString(message, [percentage]));
                },
            );

            setCalculationIsLoadingText(null);

            // If a variant is currently selected, we want to "save" its name to the calculation, since it was specifically for that variant
            const variant = getSelectedVariant(productData, selectedVariantId);
            const variantName = variant?.name;
            if (variantName) {
                responseJson.variantName = variantName;
            }

            setCalculationResult(responseJson);
            setActiveStepName('CalculationResult');
            scrollToTop();

            handleGtmEvent('price_calculation_executed');
        } catch (error) {
            setCalculationIsLoadingText(null);
            setActiveStepName('CalculationTechnicalProblem');
            console.error('error', error.message);
        }
    }

    async function handleCalculationSubmit(e) {
        e.preventDefault();

        const formEl = e.currentTarget;
        const amounts = [];
        const newAmountInputsTouched = new Set();
        const newAmountInputErrors = new Map();

        [...formEl.elements].forEach((el) => {
            const { name } = el;
            const [prefix, orgId] = name.split('__');

            if (prefix !== 'amount') {
                return;
            }

            newAmountInputsTouched.add(name);
            amounts.push({ organizationId: orgId, amount: el.value });

            const error = getAmountInputError(el, productData.unitData, resourcedTexts);
            if (error) {
                newAmountInputErrors.set(name, error);
            }
        });

        setAmountInputsTouched(newAmountInputsTouched);
        setAmountInputErrors(newAmountInputErrors);

        const hasErrors = newAmountInputErrors.size > 0;
        if (hasErrors) {
            const firstErrorName = newAmountInputErrors.keys().next().value;
            formEl.elements[firstErrorName].focus();
            return;
        }

        const formData = new FormData(formEl);
        const formDataObject = getObjectFromFormData(formData);
        const { startDate, endDate } = formDataObject;

        await handleCalculation(
            productData.id,
            startDate,
            endDate,
            amounts,
            productData.unitData.usageUnitId,
        );

        const isMobile = window.innerWidth < 640;
        if (isMobile) {
            window.location.hash = '#calculationButtons';
        }
    }

    function handleAmountInputBlur(e) {
        const isEmpty = e.target.value.length === 0;
        if (isEmpty) return;

        handleAmountInputChange(e);
        setAmountInputsTouched(new Set(amountInputsTouched).add(e.target.name));
    }

    function handleAmountInputChange({ target }) {
        const error = getAmountInputError(target, productData.unitData, resourcedTexts);
        if (error) {
            setAmountInputErrors(new Map(amountInputErrors).set(target.name, error));
        } else {
            const newAmountInputErrors = new Map(amountInputErrors);
            newAmountInputErrors.delete(target.name);
            setAmountInputErrors(newAmountInputErrors);
        }
    }

    async function handleSelectConditionalProduct(product) {
        const newSelectedOrganizationIds = [...product.organizationIds];

        if (product.unitData.isFixedForOrganizations) {
            await handleUsageAmountForOrganizations(newSelectedOrganizationIds, product.unitData);
        }

        setProductData({ ...product });
        setSelectedOrganizationsIds(newSelectedOrganizationIds);
        setActiveStepName('CalculationCalculate');
        scrollToTop();

        handleGtmEvent('price_calculation_conditional_product_select');
    }

    function handleToConditionalProductsStep() {
        setActiveStepName('CalculationConditional');
    }

    async function handleAddToCart(nrOfOrganizations) {
        setAddToCartIsLoadingText(resourcedTexts.addToCartLoader);

        try {
            const { calculationId } = calculationResult;
            await fetchAddToCart(cartAddApiUrl, calculationId);

            setShowAddedToCart(true);

            setTimeout(() => {
                setShowAddedToCart(false);

                if (productData.hasConditionalProducts) {
                    setIsInConditionalProductsFlow(true);
                    setConditionalProductsData(null);
                    setActiveStepName('CalculationConditional');
                } else {
                    setIsInConditionalProductsFlow(false);
                    setActiveStepName('CalculationFinished');
                }

                setAddToCartIsLoadingText(null);
                addToCartCount(nrOfOrganizations); // deliberately add the number of organizations and no amount, since the mini cart shows the amount of cart rules and not the total amount of products
                setCalculationResult(null);
                scrollToTop();

                handleGtmEvent('price_calculation_add_to_cart');
            }, 3000);
        } catch (error) {
            setAddToCartIsLoadingText(null);
            setActiveStepName('CalculationTechnicalProblem');
            console.error('error', error.message);
        }
    }

    const showCalculation = ['CalculationCalculate', 'CalculationResult'].includes(activeStepName);
    const showCalculationTechnicalProblem = activeStepName === 'CalculationTechnicalProblem';
    const showConditional = activeStepName === 'CalculationConditional';
    const showFinished = activeStepName === 'CalculationFinished';
    const showSchoolSelection = activeStepName === 'CalculationSchoolSelection';
    const showLogin = activeStepName === 'LoginComponent';

    return (
        <PriceCalculationContext.Provider
            value={{
                productData,
                organizationData,
                handleToSchoolSelection: () => goToSchoolSelection(),
                selectedVariantId,
                setSelectedVariantId,
                fixedAmounts,
                amountInputErrors,
                amountInputsTouched,
                handleAmountInputChange,
                handleAmountInputBlur,
                continueShoppingUrl,
                getConditionalProductsApiUrl,
                handleSelectConditionalProduct,
                handleToConditionalProductsStep,
                handleSchoolSelectionSubmit,
                sidebarData,
                activeStepName,
                calculationResult,
                handleAddToCart,
                scrollToTop,
                calculationIsLoadingText,
                addToCartIsLoadingText,
                showAddedToCart,
                cartUrl,
                conditionalProductsData,
                setConditionalProductsData,
                isInConditionalProductsFlow,
                setActiveStepName,
                fixedAmountIsLoadingText,
                selectedOrganizationsIds,
                originalSelectedOrganizationIds,
                conditionalProductsIsLoadingText,
                setConditionalProductsIsLoadingText,
                resourcedTexts,
            }}
        >
            {showLogin ? (
                <Login
                    meData={meDataProp}
                    organizationData={organizationData}
                    productData={productData}
                    sidebarData={sidebarData}
                    forgotPasswordData={forgotPasswordData}
                    selectOrganizationApiUrl={selectOrganizationApiUrl}
                    signInApiUrl={signInApiUrl}
                    loginType="priceCalculation"
                    loggedInCallback={goToSchoolSelection}
                    resourcedTexts={resourcedTexts}
                />
            ) : (
                <div className="price-calculation" ref={priceCalculationRef}>
                    <h2 className="price-calculation__title">{title}</h2>

                    <div className="row">
                        <div className="col-12 col-md-7">
                            <div className="price-calculation__main">
                                <h3 className="price-calculation__main-heading">{mainHeading}</h3>

                                {showSchoolSelection && <SchoolSelection />}

                                {showCalculation && (
                                    <form onSubmit={handleCalculationSubmit} noValidate>
                                        <Calculation />
                                    </form>
                                )}

                                {showCalculationTechnicalProblem && (
                                    <p>{resourcedTexts.calculationTechnicalProblemMessage}</p>
                                )}

                                {showConditional && <Conditional />}

                                {showFinished && <Finished />}
                            </div>
                        </div>
                        <div className="col-12 col-md-5">
                            <CalculationSidebar />
                        </div>
                    </div>
                </div>
            )}
        </PriceCalculationContext.Provider>
    );
}

export { PriceCalculation };
